import React from "react";
import CustomDropdown from "../common/CustomDropdown";
import MobileDropdown from "../search/MobileDropdown";
import MobileCountryDropdown from "../search/MobileCountryDropdown";
import DiscoveryDropdown from "./DiscoveryDropdown";
import DiscoveryCountryDropdown from "./DiscoveryCountryDropdown";
import { useMediaQuery } from "../../hooks/useMediaQuery";

const DiscoveryFilters = ({
  filters,
  setFilters,
  positions,
  filteredCountries,
  filteredDivisionCountries,
  filteredCompetitions,
  countrySearch,
  setCountrySearch,
  showCountryDropdown,
  setShowCountryDropdown,
  divisionCountrySearch,
  setDivisionCountrySearch,
  showDivisionCountryDropdown,
  setShowDivisionCountryDropdown,
  competitionSearch,
  setCompetitionSearch,
  showCompetitionDropdown,
  setShowCompetitionDropdown,
  handleCountrySelect,
  handleDivisionCountrySelect,
  handleCompetitionSelect,
  getSelectedCountryName,
  getSelectedDivisionCountryName,
  getSelectedCompetitionName,
  handleLevelChange,
  handlePositionChange,
  handleFreeAgentSinceChange,
  isFreeAgent,
  sort,
  handleSortChange,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Search-Ranking V1: values match the backend `sort` param on
  // /dashboard/free-agents/ (relevance = default). Same {value,label} choice
  // shape as FEED_SORT_CHOICES (src/constants/agentTenderChoices.js).
  const renderSortDropdown = () => {
    const options = [
      { value: "relevance", label: "Relevance" },
      { value: "recent", label: "Recently available" },
      { value: "market_value", label: "Market Value" },
    ];

    if (isMobile) {
      return (
        <MobileDropdown
          value={sort}
          onChange={handleSortChange}
          options={options}
          placeholder="Sort by"
          displayProperty="label"
          valueProperty="value"
        />
      );
    }

    return (
      <DiscoveryDropdown
        options={options}
        value={sort}
        onChange={handleSortChange}
        placeholder="Sort by"
        displayProperty="label"
        valueProperty="value"
      />
    );
  };

  const renderLevelDropdown = () => {
    const options = [
      { value: "pro", label: "Professional" },
      { value: "youth", label: "Youth" },
    ];

    if (isMobile) {
      return (
        <MobileDropdown
          value={filters.level}
          onChange={handleLevelChange}
          options={options}
          placeholder="Level"
          displayProperty="label"
          valueProperty="value"
        />
      );
    }

    return (
      <DiscoveryDropdown
        options={options}
        value={filters.level}
        onChange={handleLevelChange}
        placeholder="Level"
        displayProperty="label"
        valueProperty="value"
      />
    );
  };

  return (
    <div className="flex flex-row flex-wrap gap-x-3 gap-y-3 py-4 px-4 sm:px-6 lg:px-12 border-b mb-6 border-[#E5E6E8] items-center w-full">
      {/* Sort (Search-Ranking V1). Visible "Sort by" label (Marin: make clear
          this is a SORT, not a filter) — same label style as the Free Agents
          toggle label in DiscoveryHeader. The label is a bare "Sort by" text
          node (registered in the static bundles; the DOM translator matches
          trimmed text nodes exactly, so the colon lives in its own span).
          Mobile (375px): label is whitespace-nowrap + shrink-0, the dropdown
          shrinks via flex-1/min-w-0 — no wrap, no overflow; the row itself
          wraps as before. */}
      <div className="relative flex-1 min-w-[160px] flex items-center gap-2">
        <span className="text-sm font-medium text-[#475467] whitespace-nowrap shrink-0">
          Sort by<span>:</span>
        </span>
        <div className="relative flex-1 min-w-0">{renderSortDropdown()}</div>
      </div>

      {/* Level Filter */}
      <div className="relative flex-1 min-w-[160px]">
        {renderLevelDropdown()}
      </div>

      {/* Player Nationality Dropdown */}
      <div className="relative flex-1 min-w-[160px]">
        {isMobile ? (
          <MobileCountryDropdown
            value={filters.country || ""}
            onChange={(value) => {
              setFilters((prev) => ({ ...prev, country: value }));
              const country = filteredCountries.find(c => c.alpha3code === value || c.id === value);
              if (country) {
                handleCountrySelect(country);
              }
            }}
            options={filteredCountries}
            placeholder="Player Nationality"
            searchValue={countrySearch}
            onSearchChange={setCountrySearch}
            isOpen={showCountryDropdown}
            setIsOpen={setShowCountryDropdown}
          />
        ) : (
          <DiscoveryCountryDropdown
            value={filters.country || ""}
            onChange={(value) => setFilters((prev) => ({ ...prev, country: value }))}
            options={filteredCountries}
            placeholder="Player Nationality"
            searchValue={countrySearch || getSelectedCountryName()}
            onSearchChange={(value) => {
              setCountrySearch(value);
              if (!value) {
                setFilters((prev) => ({ ...prev, country: "" }));
              }
            }}
            isOpen={showCountryDropdown}
            setIsOpen={setShowCountryDropdown}
            onSelect={handleCountrySelect}
          />
        )}
      </div>

      {/* Division Country Dropdown */}
      <div className="relative flex-1 min-w-[160px]">
        {isMobile ? (
          <MobileCountryDropdown
            value={filters.divisionCountry || ""}
            onChange={(value) => {
              setFilters((prev) => ({ ...prev, divisionCountry: value }));
              const country = filteredDivisionCountries.find(c => c.alpha3code === value || c.id === value);
              if (country) {
                handleDivisionCountrySelect(country);
              }
            }}
            options={filteredDivisionCountries}
            placeholder="Division Country"
            searchValue={divisionCountrySearch}
            onSearchChange={setDivisionCountrySearch}
            isOpen={showDivisionCountryDropdown}
            setIsOpen={setShowDivisionCountryDropdown}
          />
        ) : (
          <DiscoveryCountryDropdown
            value={filters.divisionCountry || ""}
            onChange={(value) => setFilters((prev) => ({ ...prev, divisionCountry: value }))}
            options={filteredDivisionCountries}
            placeholder="Division Country"
            searchValue={divisionCountrySearch || getSelectedDivisionCountryName()}
            onSearchChange={(value) => {
              setDivisionCountrySearch(value);
              if (!value) {
                setFilters((prev) => ({ ...prev, divisionCountry: "" }));
              }
            }}
            isOpen={showDivisionCountryDropdown}
            setIsOpen={setShowDivisionCountryDropdown}
            onSelect={handleDivisionCountrySelect}
          />
        )}
      </div>

      {/* Competitions Dropdown */}
      <div className="relative flex-1 min-w-[160px]">
        {isMobile ? (
          <MobileDropdown
            value={filters.competition || ""}
            onChange={(value) => {
              setFilters((prev) => ({ ...prev, competition: value }));
              const competition = filteredCompetitions.find(c => c.id === value);
              if (competition) {
                handleCompetitionSelect(competition);
              }
            }}
            options={filteredCompetitions.map(c => ({
              id: c.id,
              name: c.name || c.title
            }))}
            placeholder="Division"
            displayProperty="name"
            valueProperty="id"
            noOptionsText={filters.divisionCountry ? "No competitions found" : "Select division country first"}
          />
        ) : (
          <DiscoveryDropdown
            value={filters.competition || ""}
            onChange={(value) => {
              setFilters((prev) => ({ ...prev, competition: value }));
              const competition = filteredCompetitions.find(c => c.id === value);
              if (competition) {
                handleCompetitionSelect(competition);
              }
            }}
            options={filteredCompetitions.map(c => ({
              id: c.id,
              name: c.name || c.title
            }))}
            placeholder="Division"
            displayProperty="name"
            valueProperty="id"
            noOptionsText={filters.divisionCountry ? "No competitions found" : "Select division country first"}
          />
        )}
      </div>

      {/* Free agent since - only show when free agent toggle is ON */}
      {isFreeAgent && (
        <div className="relative flex-1 min-w-[160px]">
          {isMobile ? (
            <MobileDropdown
              value={filters.freeAgentSince}
              onChange={handleFreeAgentSinceChange}
              options={[
                { value: "1-month", label: "1 month" },
                { value: "3-months", label: "3 months" },
                { value: "6-months", label: "6 months" },
              ]}
              placeholder="Free agent since"
              displayProperty="label"
              valueProperty="value"
            />
          ) : (
            <DiscoveryDropdown
              options={[
                { value: "1-month", label: "1 month" },
                { value: "3-months", label: "3 months" },
                { value: "6-months", label: "6 months" },
              ]}
              value={filters.freeAgentSince}
              onChange={handleFreeAgentSinceChange}
              placeholder="Free agent since"
              displayProperty="label"
              valueProperty="value"
            />
          )}
        </div>
      )}

      {/* Positions Dropdown */}
      <div className="relative flex-1 min-w-[160px]">
        {isMobile ? (
          <MobileDropdown
            value={filters.position}
            onChange={handlePositionChange}
            options={positions}
            placeholder="Positions"
            displayProperty="name"
            valueProperty="id"
          />
        ) : (
          <DiscoveryDropdown
            options={positions}
            value={filters.position}
            onChange={handlePositionChange}
            placeholder="Positions"
            displayProperty="name"
            valueProperty="id"
          />
        )}
      </div>

      {/* Age Range Inputs */}
      <div className="flex gap-x-3 items-center min-w-[250px] order-2 sm:order-none">
        <input
          type="number"
          placeholder="Min age (16-45)"
          className={`bg-[#F3F4F6] border border-[#D2D4D8] rounded-lg ${isMobile ? 'px-2 py-2 text-xs h-[34px]' : 'px-4 py-2 text-[16px] h-[42px]'} text-[#1E1E1ECC] font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${isMobile ? 'placeholder:text-xs' : 'placeholder:text-[16px]'} placeholder:text-[#1E1E1ECC] placeholder:font-medium w-[48%] md:w-[120px]`}
          value={filters.age_min}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              age_min: e.target.value,
            }))
          }
          min="16"
          max="45"
        />
        <div className="flex flex-shrink-0">
          <span>
            <svg
              width="5"
              height="7"
              viewBox="0 0 5 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.51172 6L1.01172 3.5L3.51172 1"
                stroke="#1E1E1E"
                strokeOpacity="0.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span>
            <svg
              width="5"
              height="7"
              viewBox="0 0 5 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.01172 6L3.51172 3.5L1.01172 1"
                stroke="#1E1E1E"
                strokeOpacity="0.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
        <input
          type="number"
          placeholder="Max age (16-45)"
          className={`bg-[#F3F4F6] border border-[#D2D4D8] rounded-lg ${isMobile ? 'px-2 py-2 text-xs h-[34px]' : 'px-4 py-2 text-[16px] h-[42px]'} text-[#1E1E1ECC] font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${isMobile ? 'placeholder:text-xs' : 'placeholder:text-[16px]'} placeholder:text-[#1E1E1ECC] placeholder:font-medium w-[48%] md:w-[120px]`}
          value={filters.age_max}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              age_max: e.target.value,
            }))
          }
          min="16"
          max="45"
        />
      </div>

      {/* Market Value Range Inputs */}
      <div className="flex gap-x-3 items-center min-w-[350px] order-3 sm:order-none">
        <input
          type="number"
          placeholder="Min market value (€)"
          className={`bg-[#F3F4F6] border border-[#D2D4D8] rounded-lg ${isMobile ? 'px-2 py-2 text-xs h-[34px]' : 'px-4 py-2 text-[16px] h-[42px]'} text-[#1E1E1ECC] font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${isMobile ? 'placeholder:text-xs' : 'placeholder:text-[16px]'} placeholder:text-[#1E1E1ECC] placeholder:font-medium w-[48%] md:w-[165px]`}
          value={filters.minMarketValue}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              minMarketValue: e.target.value,
            }))
          }
          min="0"
        />
        <div className="flex flex-shrink-0">
          <span>
            <svg
              width="5"
              height="7"
              viewBox="0 0 5 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.51172 6L1.01172 3.5L3.51172 1"
                stroke="#1E1E1E"
                strokeOpacity="0.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span>
            <svg
              width="5"
              height="7"
              viewBox="0 0 5 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.01172 6L3.51172 3.5L1.01172 1"
                stroke="#1E1E1E"
                strokeOpacity="0.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
        <input
          type="number"
          placeholder="Max market value (€)"
          className={`bg-[#F3F4F6] border border-[#D2D4D8] rounded-lg ${isMobile ? 'px-2 py-2 text-xs h-[34px]' : 'px-4 py-2 text-[16px] h-[42px]'} text-[#1E1E1ECC] font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${isMobile ? 'placeholder:text-xs' : 'placeholder:text-[16px]'} placeholder:text-[#1E1E1ECC] placeholder:font-medium w-[48%] md:w-[165px]`}
          value={filters.maxMarketValue}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              maxMarketValue: e.target.value,
            }))
          }
          min="0"
        />
      </div>
    </div>
  );
};

export default DiscoveryFilters;
